import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLElement} [lastsizeNotice]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  #selectionVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, options);
    closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateInstallment, options);
    closestSection.addEventListener('input', this.#handleQuantityChange, options);
    closestSection.addEventListener('change', this.#handleQuantityChange, options);
    this.#updateInstallment();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#selectionVersion++;
  }

  /** @param {Event} event */
  #handleQuantityChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') {
      this.#updateInstallment();
    }
  };

  #updateInstallment = () => {
    const line = this.querySelector('[data-pay-installment]');
    const amount = line?.querySelector('[data-pay-sum]');
    if (!(line instanceof HTMLElement) || !amount) return;
    const scope = this.closest('.shopify-section, dialog');
    const quantityInput = scope?.querySelector('input[name="quantity"]');
    const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) : 1;
    const priceCents = Number(line.dataset.priceCents);
    const months = Number(line.dataset.months);
    if (!Number.isFinite(priceCents) || priceCents < 0 || !Number.isFinite(months) || months <= 0) return;
    const count = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    amount.textContent = String(Math.ceil((priceCents * count) / (months * 100)));
  };

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectionVersion = ++this.#selectionVersion;
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || selectionVersion !== this.#selectionVersion || !detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote, lastsizeNotice } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Mixed-sale per-variant notice under price (Задача 1)
        const newLastsize = newProductPrice.querySelector('[ref="lastsizeNotice"]');
        if (newLastsize && lastsizeNotice) {
          lastsizeNotice.replaceWith(newLastsize);
        } else if (newLastsize && !lastsizeNotice) {
          const anchor =
            this.querySelector('[ref="volumePricingNote"]') || this.querySelector('[ref="priceContainer"]');
          anchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newLastsize.cloneNode(true)));
        } else if (!newLastsize && lastsizeNotice) {
          lastsizeNotice.remove();
        }

        const currentInstallment = this.querySelector('[data-pay-installment]');
        const newInstallment = newProductPrice.querySelector('[data-pay-installment]');
        if (newInstallment && currentInstallment) {
          currentInstallment.replaceWith(newInstallment);
        } else if (newInstallment) {
          this.append(newInstallment);
        } else {
          currentInstallment?.remove();
        }
        this.#updateInstallment();

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

